'use client'

import { useState } from 'react'
import { usePathname } from 'next/navigation'
import { motion } from 'framer-motion'
import { formatDateNormal } from '@/utils/util'
import { navigateUrl } from '@/utils/client-request'
import ReportVendorDialog from './ReportVendorDialog'

export default function Status({ vendor }: { vendor: any }) {
  const verified = !!vendor?.isDanVerified
  if (!verified) return <Unverified vendor={vendor} />
  return <Verified vendor={vendor} />
}

function Crumbs({ vendorName, verified }: { vendorName?: string, verified: boolean }) {
  const pathname = usePathname()
  return (
    <div className="cont pt-8 pb-2 text-[12px]" style={{ color: 'rgba(255,255,255,0.55)' }}>
      <a href={navigateUrl('/', pathname)} className="hover:text-white transition-colors">Нүүр</a>
      <span className="mx-1.5" style={{ opacity: 0.4 }}>/</span>
      <span>{vendorName || 'Дэлгүүр'}</span>
      <span className="mx-1.5" style={{ opacity: 0.4 }}>/</span>
      <span className="text-white">{verified ? 'Баталгаажуулалтын төлөв' : 'Анхааруулга'}</span>
    </div>
  )
}

function Field({ label, value, mono }: { label: string, value: string, mono?: boolean }) {
  return (
    <div>
      <div className="text-[10.5px] font-semibold tracking-[0.18em] uppercase" style={{ color: '#a8a8b3' }}>{label}</div>
      <div className={'mt-1.5 text-[14.5px] font-semibold text-white' + (mono ? ' font-mono tabular-nums' : '')}>{value}</div>
    </div>
  )
}

function getInitials(name?: string) {
  if (!name) return '—'
  return name.trim().split(/\s+/).map(p => p[0]).join('').slice(0, 2).toUpperCase() || name[0].toUpperCase()
}

function Verified({ vendor }: { vendor: any }) {
  const pathname = usePathname()
  const [reportOpen, setReportOpen] = useState(false)
  const openedDate = vendor?.createdAt ? formatDateNormal(new Date(vendor.createdAt)) : '—'
  const initials = getInitials(vendor?.name)
  const regNo = vendor?.regNo || `MN-DAN-${vendor?.id?.slice(0, 8).toUpperCase() || '00000000'}`
  const category = vendor?.category || '—'

  return (
    <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: 'easeIn' }}>
      <Crumbs vendorName={vendor?.name} verified />

      <div className="cont pt-6 pb-16">
        <div className="mb-10">
          <div className="max-w-xl">
            <h1 className="font-semibold tracking-[-0.025em] leading-[0.95] text-white" style={{ fontFamily: 'Gilroy, Inter, sans-serif', fontSize: 'clamp(32px, 5vw, 52px)' }}>
              Улсын бүртгэлээр баталгаажсан
            </h1>
            <p className="mt-5 text-[15px] leading-7" style={{ color: 'rgba(255,255,255,0.6)' }}>
              <span className="text-white font-medium">{vendor?.name}</span> нь Монгол Улсын Улсын бүртгэлийн ерөнхий газрын ДАН системд бүртгэгдсэн, баталгаажуулалт хүчин төгөлдөр худалдаа эрхлэгч.
            </p>
          </div>
        </div>

        <div className="relative">
          <div
            className="rounded-3xl overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, #0c0c10 0%, #16161c 100%)',
              border: '1px solid rgba(255,255,255,0.13)',
              boxShadow: '0 40px 100px -30px rgba(0,0,0,0.6)',
            }}
          >
            <div
              className="px-6 sm:px-8 py-4 flex items-center justify-between gap-4 flex-wrap"
              style={{
                background: 'linear-gradient(90deg, rgba(16,185,129,0.18), rgba(16,185,129,0.04))',
                borderBottom: '1px solid rgba(16,185,129,0.3)',
              }}
            >
              <div className="inline-flex items-center gap-2.5">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#34d399" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  <path d="m9 12 2 2 4-4" />
                </svg>
                <span className="text-[12px] font-semibold uppercase tracking-[0.22em]" style={{ color: '#34d399' }}>
                  Баталгаажуулалтын гэрчилгээ
                </span>
              </div>
              <div className="text-[11px] font-mono tabular-nums" style={{ color: 'rgba(255,255,255,0.6)' }}>
                {regNo}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-[240px_1fr]">
              <div
                className="p-8 flex flex-col items-center justify-center text-center"
                style={{ borderRight: '1px solid rgba(255,255,255,0.07)', background: 'rgba(0,0,0,0.3)' }}
              >
                <div
                  className="w-28 h-28 rounded-2xl inline-flex items-center justify-center text-[44px] font-semibold text-white"
                  style={{
                    background: 'linear-gradient(180deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02))',
                    border: '1px solid rgba(255,255,255,0.12)',
                    fontFamily: 'Gilroy, Inter, sans-serif',
                  }}
                >
                  {initials}
                </div>
                <div className="mt-5 text-[15px] font-semibold text-white" style={{ fontFamily: 'Gilroy, Inter, sans-serif' }}>{vendor?.name}</div>
                <div className="mt-1 text-[11px]" style={{ color: 'rgba(255,255,255,0.55)' }}>{category}</div>
              </div>

              <div className="p-8">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
                  <Field label="Дэлгүүрийн нэр" value={vendor?.name || '—'} />
                  <Field label="Ангилал" value={category} />
                  <Field label="Нээгдсэн огноо" value={openedDate} />
                  <Field label="Гэрчилгээний дугаар" value={regNo} mono />
                </div>
              </div>
            </div>

            <div
              className="px-6 sm:px-8 py-3 font-mono text-[10px] tabular-nums tracking-[0.12em] flex items-center justify-between gap-4"
              style={{
                background: 'rgba(0,0,0,0.4)',
                borderTop: '1px solid rgba(255,255,255,0.07)',
                color: 'rgba(255,255,255,0.55)',
              }}
            >
              <span className="truncate">{`<<MN<DAN<${(vendor?.name || 'SHOP').toUpperCase()}<<<<<<<<<<<<<<<<<<<<<<<<<<<<`.slice(0, 80)}</span>
              <span className="shrink-0">{regNo}</span>
            </div>
          </div>
        </div>

        <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
          <a
            href={navigateUrl('/', pathname)}
            className="h-12 px-7 rounded-full text-[14px] font-semibold inline-flex items-center gap-2 transition-opacity hover:opacity-90"
            style={{ background: 'white', color: '#0a0a0c' }}
          >
            Дэлгүүр үзэх
          </a>
          <button
            type="button"
            onClick={() => setReportOpen(true)}
            className="h-12 px-5 rounded-full text-[12.5px] font-medium cursor-pointer transition-colors"
            style={{ background: 'transparent', color: 'rgba(255,255,255,0.6)' }}
          >
            Мэдэгдэх
          </button>
        </div>
      </div>

      <ReportVendorDialog open={reportOpen} onClose={() => setReportOpen(false)} vendorId={vendor?.id} vendorName={vendor?.name} />
    </motion.div>
  )
}

function Unverified({ vendor }: { vendor: any }) {
  const pathname = usePathname()
  const [reportOpen, setReportOpen] = useState(false)

  const tips = [
    { t: 'Төлбөрийг нягтлан шалгах', s: 'Боломжтой бол QPay, StorePay зэрэг хамгаалагдсан төлбөрийн аргыг сонгоно уу.' },
    { t: 'Үнэлгээ, сэтгэгдлийг шалгах', s: 'Бусад худалдан авагчдын үнэлгээ, сэтгэгдлийг бүтээгдэхүүний хуудаснаас уншина уу.' },
    { t: 'Холбоо барих мэдээллийг харгалзах', s: 'Утас, имэйл, хаягийн мэдээлэл байгаа эсэхийг шалгана уу.' },
    { t: 'Эргэлзвэл худалдан авахгүй байх', s: 'Худалдан авалт хийхээсээ өмнө найдвартай эх сурвалжаас лавлана уу.' },
  ]

  return (
    <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: 'easeIn' }}>
      <Crumbs vendorName={vendor?.name} verified={false} />

      <div className="cont pb-20">
        <div className="pt-6 grid gap-12 grid-cols-1 lg:grid-cols-[1.4fr_1fr]">
          <div>
            <h1
              className="font-semibold tracking-[-0.03em] leading-[0.95] text-white"
              style={{ fontFamily: 'Gilroy, Inter, sans-serif', fontSize: 'clamp(40px, 8vw, 84px)' }}
            >
              <span style={{ color: '#fda4af', fontStyle: 'italic', fontWeight: 500 }}>{vendor?.name}</span><br />
              баталгаажаагүй.
            </h1>
            <p className="mt-8 text-[17px] leading-8 max-w-xl" style={{ color: 'rgba(255,255,255,0.6)' }}>
              Энэ дэлгүүрийн эзэмшигчийг Монгол Улсын Улсын бүртгэлд тулгуурлан баталгаажуулаагүй байна.
            </p>
          </div>

          <aside>
            <h2 className="font-semibold tracking-[-0.02em] leading-tight text-white" style={{ fontFamily: 'Gilroy, Inter, sans-serif', fontSize: 26 }}>
              Худалдан авахаасаа өмнө
            </h2>

            <ol className="mt-7 space-y-7">
              {tips.map((t, i) => (
                <li key={i} className="flex items-start gap-5">
                  <div className="font-semibold tabular-nums leading-none shrink-0" style={{ fontFamily: 'Gilroy, Inter, sans-serif', fontSize: 40, color: '#fda4af' }}>
                    0{i + 1}
                  </div>
                  <div className="pt-1.5">
                    <div className="text-[14px] font-semibold text-white">{t.t}</div>
                    <p className="mt-1.5 text-[13px] leading-6" style={{ color: 'rgba(255,255,255,0.6)' }}>{t.s}</p>
                  </div>
                </li>
              ))}
            </ol>

            <div className="mt-10 pt-6" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
              <div className="flex flex-col gap-2">
                <a
                  href={navigateUrl('/', pathname)}
                  className="h-12 rounded-full text-[13.5px] font-semibold inline-flex items-center justify-center transition-opacity hover:opacity-90"
                  style={{ background: 'white', color: '#0a0a0c' }}
                >
                  Дэлгүүрийг үзэх
                </a>
                <button
                  type="button"
                  onClick={() => setReportOpen(true)}
                  className="h-12 rounded-full text-[13px] font-medium cursor-pointer transition-colors"
                  style={{ background: 'transparent', color: 'white', border: '1px solid rgba(255,255,255,0.2)' }}
                >
                  Дэлгүүрийг мэдэгдэх
                </button>
              </div>
            </div>
          </aside>
        </div>

        <p className="mt-16 text-center text-[12px] max-w-2xl mx-auto" style={{ color: 'rgba(255,255,255,0.4)' }}>
          Энэ хуудас нь зөвхөн мэдээлэл өгөх зорилготой ба худалдан авалтын аливаа баталгаа болохгүй. Худалдаа эрхлэгч хүсэлт гаргавал хувийн мэдээллээ баталгаажуулж энэхүү төлөвөө өөрчилж болно.
        </p>
      </div>

      <ReportVendorDialog open={reportOpen} onClose={() => setReportOpen(false)} vendorId={vendor?.id} vendorName={vendor?.name} />
    </motion.div>
  )
}
